import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateContactData, deleteContact } from '../../Action/api';
import { 
  selectContactByDateTimeStamp, 
  updateContactInStore,
  deleteContactFromStore,
  fetchContacts,
  selectCurrentCountry,
  selectCurrentMonthYear,
} from '../../Redux/contactsSlice';
import {
  ContactExtraSections,
  DetailField,
  getExtraInitialValues,
  buildExtraPayload,
  validateExtraFields,
  COUNTRY_OPTIONS,
  INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
} from '../../Components/ContactForm';
import CopyButton from '../../Components/CopyButton';
import { ActivityTimeline, DataQualityPanel, newActivityId } from '../../Components/ContactActivity';
import { getCurrentUser } from '../../Utils/auth';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  Person,
  Business,
  Email,
  Phone,
  WhatsApp,
  Language,
  LinkedIn,
  LocationOn,
  Work,
  Notes,
  LocalOffer,
  Public,
} from '@mui/icons-material';

// Form values for a contact (or blanks while it loads)
const buildFormData = (contact) => ({
  // Person fields
  fullName: contact?.fullName || '',
  email: contact?.email || '',
  phoneNumber: contact?.phoneNumber || '',
  whatsappNumber: contact?.whatsappNumber || '',
  jobTitle: contact?.jobTitle || '',
  linkedinProfile: contact?.linkedinProfile || '',
  gender: contact?.gender || '',
  // Company fields
  companyName: contact?.companyName || '',
  companyEmail: contact?.companyEmail || '',
  companyPhone: contact?.companyPhone || '',
  companyWhatsapp: contact?.companyWhatsapp || '',
  industry: contact?.industry || '',
  companySize: contact?.companySize || '',
  // Shared fields
  website: contact?.website || '',
  country: contact?.country || '',
  state: contact?.state || '',
  district: contact?.district || '',
  city: contact?.city || '',
  postcode: contact?.postcode || '',
  address: contact?.address || '',
  notes: contact?.notes || '',
  tags: contact?.tags || [],
  // Extended profile, education and relationship fields
  ...getExtraInitialValues(contact),
});

// The fields the update API accepts. Built from form-shaped values so it never includes
// server-managed attributes (updatedAt, createdAt, dateTimeStamp, contactId) – sending
// those makes DynamoDB reject the update ("Two document paths overlap ... updatedAt").
const buildUpdatePayload = (values, contactType, tracking) => ({
  contactType,
  ...values,
  tags: Array.isArray(values.tags)
    ? values.tags
    : values.tags
    ? values.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : [],
  ...buildExtraPayload(values, contactType),
  activities: tracking?.activities || [],
  dataQuality: tracking?.dataQuality || null,
  nextFollowUpAt: tracking?.nextFollowUpAt || null,
});

const ContactDetails = () => {
  const { dateTimeStamp } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // Get contact from Redux store
  const decodedTimestamp = decodeURIComponent(dateTimeStamp);
  const contactFromRedux = useSelector(selectContactByDateTimeStamp(decodedTimestamp));
  const currentCountry = useSelector(selectCurrentCountry);
  const currentMonthYear = useSelector(selectCurrentMonthYear);

  // Contact data from Redux first, fallback to navigation state.
  // `localPatch` holds activity / data-quality changes saved on this page, so they show
  // immediately even if the contact isn't in the Redux list (e.g. opened via a link).
  const storedContact = contactFromRedux || location.state?.contact || null;
  const [localPatch, setLocalPatch] = useState({});
  const initialContact = useMemo(
    () => (storedContact ? { ...storedContact, ...localPatch } : null),
    [storedContact, localPatch]
  );
  const currentUser = useMemo(() => getCurrentUser(), []);
  const [activitySaving, setActivitySaving] = useState(false);
  const [qualitySaving, setQualitySaving] = useState(false);

  const [isEditMode, setIsEditMode] = useState(Boolean(location.state?.edit));
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Form data
  const [contactType, setContactType] = useState(initialContact?.contactType || 'person');
  const [formData, setFormData] = useState(() => buildFormData(initialContact));

  // The contact may arrive after mount (fetched into Redux) – fill the form once it does
  const loadedContactKey = initialContact?.dateTimeStamp;
  useEffect(() => {
    if (initialContact && !isEditMode) {
      setFormData(buildFormData(initialContact));
      setContactType(initialContact.contactType || 'person');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedContactKey]);

  const [errors, setErrors] = useState({});

  // Load contact from Redux if not available
  useEffect(() => {
    if (!initialContact) {
      console.log('Redux: Contact not in store, attempting to fetch');
      // If we have country and monthYear, try to fetch the data
      if (currentCountry && currentMonthYear) {
        dispatch(fetchContacts({ country: currentCountry, monthYear: currentMonthYear }))
          .unwrap()
          .then(() => {
            console.log('Redux: Contacts fetched, contact should be available now');
          })
          .catch((error) => {
            console.error('Redux: Failed to fetch contacts', error);
            setSnackbar({
              open: true,
              message: 'Contact not found',
              severity: 'error'
            });
            setTimeout(() => navigate('/contact-management'), 2000);
          });
      } else {
        // No way to fetch, redirect to contact management
        setSnackbar({
          open: true,
          message: 'Contact not found',
          severity: 'error'
        });
        setTimeout(() => navigate('/contact-management'), 2000);
      }
    }
  }, [initialContact, currentCountry, currentMonthYear, dispatch, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors for this field (and its nested education entries) when the user edits it
    setErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key === field || key.startsWith(`${field}.`)) delete next[key];
      });
      return next;
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[1-9]\d{9,14}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};

    // Mandatory fields based on contact type
    if (contactType === 'person') {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
    } else {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    // Optional field validations (only if filled)
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.companyEmail && !validateEmail(formData.companyEmail)) {
      newErrors.companyEmail = 'Invalid email format';
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Invalid phone format (include country code)';
    }

    if (formData.companyPhone && !validatePhone(formData.companyPhone)) {
      newErrors.companyPhone = 'Invalid phone format (include country code)';
    }

    if (formData.whatsappNumber && !validatePhone(formData.whatsappNumber)) {
      newErrors.whatsappNumber = 'Invalid WhatsApp format (include country code)';
    }

    if (formData.companyWhatsapp && !validatePhone(formData.companyWhatsapp)) {
      newErrors.companyWhatsapp = 'Invalid WhatsApp format (include country code)';
    }

    Object.assign(newErrors, validateExtraFields(formData, contactType));

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors before saving',
        severity: 'error'
      });
      setTimeout(() => {
        document.querySelector('.Mui-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    setLoading(true);

    try {
      // Prepare the contact payload
      // Activities / data quality aren't part of the form – carry them over so saving never drops them
      const contactPayload = buildUpdatePayload(formData, contactType, initialContact);

      const result = await updateContactData(formData.country, decodeURIComponent(dateTimeStamp), contactPayload);

      if (result.success) {
        // Update Redux store
        dispatch(updateContactInStore({
          ...initialContact,
          ...contactPayload,
          dateTimeStamp: decodedTimestamp,
        }));
        
        setSnackbar({
          open: true,
          message: 'Contact updated successfully!',
          severity: 'success'
        });
        setIsEditMode(false);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to update contact',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update contact',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Save a partial change (activity, follow-up, data quality) on top of the stored contact
  const saveContactPatch = async (patch, successMessage) => {
    // Start from the saved contact (not unsaved form edits), then apply the change
    const storedType = initialContact.contactType || 'person';
    const payload = {
      ...buildUpdatePayload(buildFormData(initialContact), storedType, initialContact),
      ...patch,
    };
    try {
      const result = await updateContactData(initialContact.country || formData.country, decodedTimestamp, payload);
      if (!result.success) {
        setSnackbar({ open: true, message: result.message || 'Could not save. Please try again.', severity: 'error' });
        return false;
      }
      setLocalPatch((prev) => ({ ...prev, ...patch }));
      dispatch(updateContactInStore({ ...payload, dateTimeStamp: decodedTimestamp }));
      if (successMessage) setSnackbar({ open: true, message: successMessage, severity: 'success' });
      return true;
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Could not save. Please try again.', severity: 'error' });
      return false;
    }
  };

  const currentActivities = () => (Array.isArray(initialContact?.activities) ? initialContact.activities : []);

  const handleAddActivity = async (entry) => {
    setActivitySaving(true);
    const patch = { activities: [...currentActivities(), entry] };
    if (entry.followUpAt) patch.nextFollowUpAt = entry.followUpAt;
    const ok = await saveContactPatch(patch, entry.followUpAt ? 'Added to timeline · follow-up scheduled' : 'Added to timeline');
    setActivitySaving(false);
    return ok;
  };

  const handleDeleteActivity = async (id) => {
    setActivitySaving(true);
    await saveContactPatch({ activities: currentActivities().filter((a) => a.id !== id) }, 'Entry deleted');
    setActivitySaving(false);
  };

  // iso = new date; done = mark the current follow-up complete (logged on the timeline)
  const handleSetFollowUp = async (iso, done = false) => {
    setActivitySaving(true);
    if (done) {
      const entry = {
        id: newActivityId(),
        type: 'note',
        text: 'Follow-up completed',
        outcome: null,
        createdAt: new Date().toISOString(),
        authorId: currentUser.id,
        authorName: currentUser.name,
        followUpAt: null,
      };
      await saveContactPatch({ nextFollowUpAt: null, activities: [...currentActivities(), entry] }, 'Follow-up marked as done');
    } else {
      await saveContactPatch({ nextFollowUpAt: iso }, 'Follow-up date updated');
    }
    setActivitySaving(false);
  };

  const handleSaveQuality = async (dataQuality) => {
    setQualitySaving(true);
    await saveContactPatch({ dataQuality }, 'Marketing & data quality saved');
    setQualitySaving(false);
  };

  const handleCancel = () => {
    // Reset form data to initial values
    setFormData(buildFormData(initialContact));
    setContactType(initialContact?.contactType || 'person');
    setErrors({});
    setIsEditMode(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteDialogOpen(false);

    try {
      const result = await deleteContact(formData.country, decodedTimestamp);

      if (result.success) {
        // Remove from Redux store
        dispatch(deleteContactFromStore(decodedTimestamp));
        
        setSnackbar({
          open: true,
          message: 'Contact deleted successfully!',
          severity: 'success'
        });

        // Navigate back to contact management after a short delay
        setTimeout(() => {
          navigate('/contact-management');
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete contact',
          severity: 'error'
        });
        setDeleting(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete contact',
        severity: 'error'
      });
      setDeleting(false);
    }
  };

  const industries = INDUSTRY_OPTIONS;

  const companySizes = COMPANY_SIZE_OPTIONS;

  const countries = COUNTRY_OPTIONS;

  if (!initialContact) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 4 }}>
      <Container maxWidth="lg">
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={() => navigate('/contact-management')} sx={{ bgcolor: '#fff' }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Contact Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {isEditMode ? 'Edit contact information' : 'View contact information'}
              </Typography>
            </Box>
          </Stack>

          {!isEditMode ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
                onClick={handleDeleteClick}
                disabled={deleting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    borderColor: '#dc2626',
                    bgcolor: '#fef2f2',
                  },
                }}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setIsEditMode(true)}
                sx={{
                  bgcolor: '#3b82f6',
                  '&:hover': { bgcolor: '#2563eb' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Edit Contact
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSave}
                disabled={loading}
                sx={{
                  bgcolor: '#10b981',
                  '&:hover': { bgcolor: '#059669' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Save Changes
              </Button>
            </Stack>
          )}
        </Stack>

        {/* Contact Type Badge */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Chip
              icon={contactType === 'person' ? <Person /> : <Business />}
              label={contactType === 'person' ? 'Individual Person' : 'Company'}
              sx={{
                bgcolor: contactType === 'person' ? '#3b82f615' : '#8b5cf615',
                color: contactType === 'person' ? '#3b82f6' : '#8b5cf6',
                fontWeight: 700,
                fontSize: '0.9rem',
                py: 2.5,
                px: 1,
              }}
            />
            {initialContact?.contactId && (
              <Box sx={{ 
                bgcolor: '#f8fafc', 
                px: 2, 
                py: 1, 
                borderRadius: 1.5, 
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                  Contact ID:
                </Typography>
                <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 700, fontSize: '0.875rem' }}>
                  {initialContact.contactId}
                </Typography>
                <CopyButton value={initialContact.contactId} label="Copy contact ID" />
              </Box>
            )}
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Added on {new Date(initialContact.createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
          </Stack>
        </Paper>

        {/* Follow-up timeline + marketing / data quality */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <ActivityTimeline
              activities={initialContact.activities || []}
              nextFollowUpAt={initialContact.nextFollowUpAt}
              currentUser={currentUser}
              onAdd={handleAddActivity}
              onDelete={handleDeleteActivity}
              onSetFollowUp={handleSetFollowUp}
              saving={activitySaving}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <DataQualityPanel
              contact={initialContact}
              onSave={handleSaveQuality}
              saving={qualitySaving}
              currentUser={currentUser}
            />
          </Grid>
        </Grid>

        {/* Main Form */}
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
          {/* Contact Type Toggle - Only in edit mode */}
          {isEditMode && (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                  Contact Type
                </Typography>
                <ToggleButtonGroup
                  value={contactType}
                  exclusive
                  onChange={(e, value) => value && setContactType(value)}
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                  <ToggleButton 
                    value="person"
                    sx={{
                      px: { xs: 2, sm: 4 },
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    <Person sx={{ mr: 1 }} /> Individual Person
                  </ToggleButton>
                  <ToggleButton 
                    value="company"
                    sx={{
                      px: { xs: 2, sm: 4 },
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    <Business sx={{ mr: 1 }} /> Company
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Divider sx={{ mb: 4 }} />
            </>
          )}

          {/* Person Fields */}
          {contactType === 'person' && (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: 1, color: '#3b82f6' }} /> Personal Information
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Full Name"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    select
                    label="Gender"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    readOnly={!isEditMode}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  >
                    {['Male', 'Female', 'Other'].map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </DetailField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Phone Number"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber || 'Include country code (e.g., +91)'}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="WhatsApp Number"
                    value={formData.whatsappNumber}
                    onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.whatsappNumber}
                    helperText={errors.whatsappNumber || 'Include country code (e.g., +91)'}
                    InputProps={{
                      startAdornment: <WhatsApp sx={{ mr: 1, color: '#25D366' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Job Title"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                    readOnly={!isEditMode}
                    InputProps={{
                      startAdornment: <Work sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="LinkedIn Profile"
                link
                    value={formData.linkedinProfile}
                    onChange={(e) => handleInputChange('linkedinProfile', e.target.value)}
                    readOnly={!isEditMode}
                    InputProps={{
                      startAdornment: <LinkedIn sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Company Fields */}
          {contactType === 'company' && (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center' }}>
                <Business sx={{ mr: 1, color: '#8b5cf6' }} /> Company Information
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Company Name"
                    required
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                    InputProps={{
                      startAdornment: <Business sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Company Email"
                    type="email"
                    value={formData.companyEmail}
                    onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.companyEmail}
                    helperText={errors.companyEmail}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Company Phone"
                    value={formData.companyPhone}
                    onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.companyPhone}
                    helperText={errors.companyPhone || 'Include country code (e.g., +91)'}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    label="Company WhatsApp"
                    value={formData.companyWhatsapp}
                    onChange={(e) => handleInputChange('companyWhatsapp', e.target.value)}
                    readOnly={!isEditMode}
                    error={!!errors.companyWhatsapp}
                    helperText={errors.companyWhatsapp || 'Include country code (e.g., +91)'}
                    InputProps={{
                      startAdornment: <WhatsApp sx={{ mr: 1, color: '#25D366' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    select
                    label="Industry"
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    readOnly={!isEditMode}
                  >
                    {industries.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </DetailField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    fullWidth
                    select
                    label="Company Size"
                    value={formData.companySize}
                    onChange={(e) => handleInputChange('companySize', e.target.value)}
                    readOnly={!isEditMode}
                  >
                    {companySizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </DetailField>
                </Grid>
              </Grid>
            </>
          )}

          {/* Extended profile: personal / business details, education, relationship */}
          <ContactExtraSections
            contactType={contactType}
            formData={formData}
            errors={errors}
            onFieldChange={handleInputChange}
            readOnly={!isEditMode}
          />

          <Divider sx={{ my: 4 }} />

          {/* Shared Fields */}
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ mr: 1, color: '#10b981' }} /> Additional Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                label="Website"
                link
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <Language sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                select
                label="Country"
                required
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                readOnly={!isEditMode}
                error={!!errors.country}
                helperText={errors.country}
                InputProps={{
                  startAdornment: <Public sx={{ mr: 1, color: '#64748b' }} />,
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </DetailField>
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                label="State/Province"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                label="District"
                value={formData.district}
                onChange={(e) => handleInputChange('district', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DetailField
                fullWidth
                label="Postcode/ZIP"
                value={formData.postcode}
                onChange={(e) => handleInputChange('postcode', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DetailField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DetailField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                readOnly={!isEditMode}
                InputProps={{
                  startAdornment: <Notes sx={{ mr: 1, color: '#64748b', alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <DetailField
                fullWidth
                label="Tags"
                value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                onChange={(e) => handleInputChange('tags', e.target.value)}
                readOnly={!isEditMode}
                helperText="Separate tags with commas"
                InputProps={{
                  startAdornment: <LocalOffer sx={{ mr: 1, color: '#64748b' }} />,
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
            Delete Contact
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#64748b' }}>
              Are you sure you want to delete this contact? This action cannot be undone.
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fee2e2' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                {contactType === 'person' ? formData.fullName : formData.companyName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {contactType === 'person' ? formData.email : formData.companyEmail}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              sx={{ textTransform: 'none', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#ef4444',
                '&:hover': { bgcolor: '#dc2626' },
              }}
            >
              {deleting ? 'Deleting...' : 'Delete Contact'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ContactDetails;
